import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { take, takeUntil } from 'rxjs/operators';
import { ShipmentAssignedFleetsType } from '@shared-features/enums/shipment-assigned-fleets-type.enum';
import { CustomerFleetType } from '@shared-features/enums/customer-fleet-type.enum';
import { DriverTrackingStatus } from '@shared-features/enums/driver-tracking-status.enum';
import { ConfigConstant } from '@shared/constants/config.constant';
import { Tracking } from '@features/live-tracking/models/tracking-driver';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { DriversTrackingService } from '@shared-features/services/drivers-tracking.service';
import { SharedConstants } from '@shared/model/shared-constants';
import { SectionStateStatus } from '@shared/enums/section-state-status.enum';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { DriversCommonApiService } from '@shared-features/services/drivers-common-api.service';
import { SharedApiService } from '@shared-features/services/shared-api.service';
import { DriverTimeFiltration } from '@shared-features/enums/driver-time-filtration.enum';
import { Subscription } from 'rxjs';
import { ShipmentsListType } from '@shared/enums/shipments-list-type.enum';
import { IconType } from '@shared/enums/icon-type.enum';
import { LocationsApiService } from '@shared-features/services/locations-api.service';
import { DriversApiService } from '@features/fleets/services/drivers-api.service';

@Component({
  selector: 'app-drivers-tracking-map',
  templateUrl: './drivers-tracking-map.component.html',
  styleUrls: ['./drivers-tracking-map.component.scss'],
})
export class DriversTrackingMapComponent extends BaseComponent implements OnInit, OnDestroy {
  @Output() selectDriverEvent = new EventEmitter<Tracking>();
  @Output() totalFilteredDrivers = new EventEmitter<number>();
  @Input() filterBy: DriverTimeFiltration = DriverTimeFiltration.LastHour;
  @Input() showSelectButton = true;
  @Input() filterByStatus: DriverTrackingStatus = DriverTrackingStatus.ALL;
  @Input() fleetType: ShipmentAssignedFleetsType = ShipmentAssignedFleetsType.All;
  @Input() contentFillTheScreen: boolean = false;
  @Input() selectedFleetId: any;
  showFadeIn = false;

  public latitude: number = null;
  public longitude: number = null;
  public zoom = 6;
  public drivers: Tracking[] = [];
  public selectedDriver: Tracking;
  public filteredDrivers: Tracking[] = [];
  sharedConstants = SharedConstants;
  routesUtil = RoutesUtil;
  driverIdsAccordingToFleetType: number[] = [];
  trackingSubscription: Subscription;
  googleMapLoaded = false;

  protected readonly ShipmentsListType = ShipmentsListType;
  protected readonly IconType = IconType;

  constructor(
    private trackingService: DriversTrackingService,
    private sharedApiService: SharedApiService,
    private driversCommonApiService: DriversCommonApiService,
    private changeDetectorRef: ChangeDetectorRef,
    private locationApiService: LocationsApiService,
    private driversApiService: DriversApiService
  ) {
    super();
  }

  ngOnInit(): void {
    navigator.permissions.query({ name: 'geolocation' }).then((data) => {
      this.centeringTheMapToSaudiArabia();
    });

    this.getLatestDriversLocation();
    this.subscribeTrackingDriverEvent();

    this.locationApiService.loadGoogleMapsApi().then(() => {
      this.googleMapLoaded = true;
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.filterBy && changes.filterBy.currentValue) ||
      (changes.filterByStatus && changes.filterByStatus.currentValue) ||
      (changes.fleetType && changes.fleetType.currentValue) ||
      (changes.selectedFleetId && changes.selectedFleetId.currentValue)
    ) {
      this.sectionState = SectionStateStatus.LoadingTransparent;
      if (this.selectedFleetId === 'All') this.selectedFleetId = null;
      this.filterDrivers();
    }
  }

  getLatestDriversLocation(): void {
    this.sharedApiService
      .getLatestDriversLocation()
      .pipe(take(1))
      .subscribe(
        (drivers) => {
          this.drivers = [...drivers];
          this.filterDrivers();
        },
        (error) => {}
      );
    this.changeDetectorRef.markForCheck();
  }

  subscribeTrackingDriverEvent(): void {
    if (this.trackingSubscription) {
      this.trackingSubscription.unsubscribe();
    }
    this.trackingSubscription = this.trackingService
      .listen(SharedConstants.TRACKING_DRIVER_EVENT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          res.selected = false;
          const previousDriver = this.drivers.find((driver) => driver.driverId === res.driverId);
          if (previousDriver) {
            previousDriver.latitude = res.latitude;
            previousDriver.longitude = res.longitude;
            previousDriver.createdAt = res.createdAt;
            previousDriver.date = res.date;
            previousDriver.shipmentId = res.shipmentId;
          } else {
            this.drivers.push(res);
            this.filterDrivers();
          }
        },
        (error) => {}
      );
  }

  onSelectDriver(item: Tracking): void {
    this.drivers.forEach((element) => (element.selected = false));
    this.selectedDriver = item;
    item.selected = true;
    this.showFadeIn = false;
    this.driversCommonApiService
      .getDriverById(item.driverId.toString())
      .pipe(take(1))
      .subscribe(
        (driver) => {
          this.selectedDriver.user = driver.user;
          this.selectedDriver.truck = driver.truck;
          this.selectedDriver.nationality = driver.nationality;
          this.selectedDriver.fleet = driver.fleet;
        },
        (error) => {
          this.asyncFeedbackService.showFailureMessage(
            error.errors ? error.errors[0] : this.translationsList['ErrorMessages']['ErrorHappened']
          );
        }
      );
    if (item.selected) {
      setTimeout(() => {
        this.showFadeIn = true;
        this.changeDetectorRef.markForCheck();
      }, 50);
    }
    this.changeDetectorRef.markForCheck();
  }
  selectDriver(): void {
    this.selectDriverEvent.emit(this.selectedDriver);
  }

  onClose(): void {
    this.drivers.forEach((element) => (element.selected = false));
  }

  filterDrivers(): void {
    this.filteredDrivers = [];
    this.filterDriverByTime();
    this.filterDriverByStatus();

    if (this.filteredDrivers.length && this.fleetType !== ShipmentAssignedFleetsType.All) {
      switch (this.fleetType) {
        case ShipmentAssignedFleetsType.Specific:
          this.getDriversAccordingToFleetType(CustomerFleetType.Specific, this.selectedFleetId);
          break;

        case ShipmentAssignedFleetsType.WithoutSpecific:
          this.getDriversAccordingToFleetType(CustomerFleetType.Global, this.selectedFleetId);
          break;
      }
    } else if (
      this.fleetType === ShipmentAssignedFleetsType.All &&
      this.filteredDrivers.length &&
      this.selectedFleetId
    ) {
      this.getDriversAccordingToFleetType(null, this.selectedFleetId);
    } else {
      this.totalFilteredDrivers.emit(this.filteredDrivers.length);
      if (this.filteredDrivers.length < 1) {
        this.centeringTheMapToSaudiArabia();
      }
      this.sectionState = SectionStateStatus.Ready;
    }
  }

  getDriversAccordingToFleetType(type?: CustomerFleetType, fleetId?: number): void {
    const data = {
      fleetType: type,
      fleetId: fleetId,
      driverIds: this.filteredDrivers.map((driver) => driver.driverId),
    };

    this.load(this.driversApiService.filterDriverByFleetType(data)).subscribe((data) => {
      this.driverIdsAccordingToFleetType = data.map((driver) => driver.id);
      this.filterDriverByFleetType();
      this.totalFilteredDrivers.emit(this.filteredDrivers.length);
      if (this.filteredDrivers.length < 1) {
        this.centeringTheMapToSaudiArabia();
      }
    });
  }

  filterDriverByTime(): void {
    this.filteredDrivers = this.drivers.filter((driver) => {
      return this.isMatchTimeRange(driver.date);
    });
  }

  filterDriverByStatus(): void {
    switch (this.filterByStatus) {
      case DriverTrackingStatus.ALL:
        this.filteredDrivers = [...this.filteredDrivers];
        break;

      case DriverTrackingStatus.AVAILABLE:
        this.filteredDrivers = this.filteredDrivers.filter((driver) => !+driver.shipmentId);
        break;

      case DriverTrackingStatus.UNAVAILABLE:
        this.filteredDrivers = this.filteredDrivers.filter((driver) => +driver.shipmentId);
        break;
    }
  }

  filterDriverByFleetType(): void {
    this.filteredDrivers = this.filteredDrivers?.filter((driver) => {
      return this.driverIdsAccordingToFleetType?.includes(driver.driverId);
    });
  }

  isMatchTimeRange(driverDate: string): boolean {
    switch (this.filterBy) {
      case DriverTimeFiltration.LastHour:
        return (
          new Date(driverDate).getTime() >=
            new Date(new Date().getTime() - 1000 * 60 * 60).getTime() &&
          this.withoutTime(new Date()) === this.withoutTime(driverDate)
        );

      case DriverTimeFiltration.LastTwoHours:
        return (
          new Date(driverDate).getTime() >=
            new Date(new Date().getTime() - 1000 * 120 * 60).getTime() &&
          this.withoutTime(new Date()) === this.withoutTime(driverDate)
        );

      case DriverTimeFiltration.LastDay:
        return (
          this.withoutTime(driverDate) >=
          this.withoutTime(new Date().valueOf() - 1000 * 60 * 60 * 24)
        );

      case DriverTimeFiltration.LastTwoDays:
        return (
          this.withoutTime(driverDate) >=
          this.withoutTime(new Date().valueOf() - 1000 * 60 * 60 * 48)
        );

      case DriverTimeFiltration.AllTime:
        return true;
    }
  }

  withoutTime(dateTime): number {
    const date = new Date(dateTime);
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  }

  centeringTheMapToSaudiArabia(): void {
    this.latitude = ConfigConstant.defaultLatitude;
    this.longitude = ConfigConstant.defaultLongitude;
    this.changeDetectorRef.markForCheck();
  }

  // private methods
  private setCurrentLocation(): void {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        this.latitude = position.coords.latitude;
        this.longitude = position.coords.longitude;
        this.zoom = 6;
      });
    }
  }

  private changeLocation(lat, lng): void {
    this.latitude = lat;
    this.longitude = lng;
  }
}
